import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { useEditor } from "@/components/editor/useEditorState";

export const RUNNING = ["running", "analyzing"];
export const usd = (n) => (n == null ? "—" : n > 0 && n < 0.01 ? "< 0,01 $" : `${n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`);
// Budget amounts: 3 decimals under 1 $ so that 0,049 $ of 0,05 $ does not read as 100%.
export const usdFine = (n) => `${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: n > 0 && n < 1 ? 3 : 2 })} $`;

export function useAiEdits(kinds) {
  const ed = useEditor();
  const { api, id } = ed;
  const [all, setAll] = useState([]);
  const [busy, setBusy] = useState("");
  const reload = useCallback(async () => { const { data } = await api.get(`/projects/${id}/ai-edits`); setAll(data); return data; }, [api, id]);
  useEffect(() => { reload().catch(() => {}); }, [reload]);
  const running = all.find((e) => RUNNING.includes(e.status));

  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(async () => {
      const { data } = await api.get(`/projects/${id}/ai-edits/${running.id}`);
      if (RUNNING.includes(data.status)) return;
      clearInterval(t);
      await reload();
      if (data.status === "cancelled") toast.message("Richiesta AI interrotta", { description: data.error });
      else if (data.status === "error" || data.error) toast.error(data.error || "Richiesta AI non riuscita");
      else if (data.status === "analyzed") toast.message("Analisi pronta: scegli gli elementi da correggere");
      else if (data.status === "ready") toast.message("Proposta pronta: controlla le differenze e applica");
    }, 2000);
    return () => clearInterval(t);
  }, [running?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); if (ok) toast.success(ok); return r; } catch (e) { toast.error(errMsg(e)); return null; } finally { setBusy(""); }
  };
  const create = (body, est) => run("create", async () => {
    await ed.save();
    const { data } = await api.post(`/projects/${id}/ai-edits`, { ...body, confirm_usd: est?.usd });
    setAll((xs) => [data, ...xs]);
    return data;
  });
  const startFix = (eid, body, est) => run("fix", async () => {
    await ed.save();
    await api.post(`/projects/${id}/ai-edits/${eid}/fix`, { ...body, confirm_usd: est?.usd });
    await reload();
  });
  const apply = (e, exclude = []) => run("apply", async () => {
    await ed.save();
    const { data } = await api.post(`/projects/${id}/ai-edits/${e.id}/apply`, { exclude });
    ed.replaceContent(data.content, { keepHistory: true });
    ed.setAiPreview(null);
    ed.setProject((p) => ({ ...p, updated_at: new Date().toISOString() }));
    await reload();
  }, "Modifica applicata · versione precedente salvata");
  const discard = (e) => run("discard", async () => {
    await api.post(`/projects/${id}/ai-edits/${e.id}/discard`);
    if (ed.aiPreview?.id === e.id) ed.setAiPreview(null);
    await reload();
  }, "Proposta scartata");
  const undo = async (e, force = false) => {
    setBusy("undo");
    try {
      await ed.save();
      const { data } = await api.post(`/projects/${id}/ai-edits/${e.id}/undo`, { force });
      ed.replaceContent(data.content, { keepHistory: true });
      await reload();
      toast.success("Modifica annullata");
      return "ok";
    } catch (err) {
      const msg = errMsg(err);
      if (err?.response?.status === 409 && msg.startsWith("MODIFIED:")) return msg.replace("MODIFIED: ", "");
      toast.error(msg);
      return null;
    } finally { setBusy(""); }
  };
  const preview = (e, exclude = []) => run("preview", async () => {
    await ed.save();
    const { data } = await api.get(`/projects/${id}/ai-edits/${e.id}/preview`, { params: { exclude: exclude.join(",") } });
    ed.setAiPreview({ id: e.id, label: e.label, content: data.content, stale: data.stale });
  });

  return { edits: all.filter((e) => kinds.includes(e.kind)), running, busy, create, startFix, apply, discard, undo, preview, reload };
}
